import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { ArrowRight, Loader2 } from "lucide-react";
import PublicLayout from "@/layouts/PublicLayout";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { buildRequestUrl, readRequestContext } from "@/lib/requestContext";

// Clean, shareable entry URLs: /request-service and /request-service/:slug (?source=, utm_*).
export default function RequestEntryPage() {
  const { slug } = useParams();
  const [sp] = useSearchParams();
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  const [target, setTarget] = useState(null);
  const [missing, setMissing] = useState(false);

  useEffect(() => {
    if (!slug) { setTarget({}); return; }
    api.get("/request-entry/resolve", { params: { slug } }).then((r) => setTarget(r.data)).catch(() => { setMissing(true); setTarget({}); });
  }, [slug]);

  const url = target ? buildRequestUrl({
    ...readRequestContext(sp),
    request_source: sp.get("source") || sp.get("utm_source") || (slug ? "request_url" : "request_cta"),
    category_id: target.category?.id, subcategory_id: target.subcategory?.id,
  }) : null;

  useEffect(() => { if (url && user && !loading && !missing) navigate(url, { replace: true }); }, [url, user, loading, missing, navigate]);

  const name = target?.subcategory?.name || target?.category?.name;
  return (
    <PublicLayout>
      <section className="relative -mt-24 pt-24 bg-[#071b22] text-white min-h-[70vh] flex items-center" data-testid="request-entry-page">
        <div className="mx-auto max-w-3xl px-6 py-20 text-center">
          {!target ? <Loader2 className="h-6 w-6 animate-spin mx-auto" /> : (
            <>
              <div className="text-xs font-bold tracking-[0.22em] uppercase text-[#87bccc]">Request a service</div>
              <h1 className="mt-4 font-display text-4xl md:text-5xl font-semibold tracking-tight" data-testid="request-entry-title">
                {name ? `Let's Get Your ${name} Project Started` : "Tell Us What You Need"}
              </h1>
              <p className="mt-5 text-white/70 text-lg">{missing ? "We couldn't find that service, but you can describe it in your own words." : "Answer a few quick questions and qualified Gulf Coast pros will send you estimates."}</p>
              <Link to={url} className="mt-10 inline-flex items-center gap-2 rounded-full bg-white px-7 py-4 text-base font-semibold text-[#071b22] hover:bg-[#e8f3f6]" data-testid="request-entry-start">
                {user ? "Continue" : "Sign in to start"} <ArrowRight className="h-4 w-4" />
              </Link>
            </>
          )}
        </div>
      </section>
    </PublicLayout>
  );
}
